import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { AuditLoggers } from '../../../utils/auditLogger';
import {
  harmonateDrawerHeaderSx,
  harmonateDialogCloseIconSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
  harmonateDrawerBodySx,
  harmonateDrawerAccordionGroupSx,
  harmonateDrawerAccordionSx,
  harmonateDrawerAccordionSummarySx,
  harmonateDrawerAccordionTitleSx,
  harmonateDrawerAccordionDetailsSx,
  harmonateDrawerAddIconSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';

const UserRolesDrawer = ({ open, onClose, userId, username }) => {
  const { canAdd, canDelete } = useFunctionPermissions(FC.USERS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [masterRoles, setMasterRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const [propertyRows, setPropertyRows] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [propertyError, setPropertyError] = useState(null);
  const [activeProperties, setActiveProperties] = useState([]);
  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const [propertyPickerQuery, setPropertyPickerQuery] = useState('');
  const [submittingProperty, setSubmittingProperty] = useState(false);
  const [loadingPropertyOptions, setLoadingPropertyOptions] = useState(false);
  const [deletePropertyDialogOpen, setDeletePropertyDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [expanded, setExpanded] = useState('roles');

  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();

  useEffect(() => {
    if (!userId || !open) return;
    fetchMasterRoles();
    fetchUserRoles();
    fetchUserTenantPropertiesList();
    // eslint-disable-next-line
  }, [userId, open]);

  const fetchMasterRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) throw new Error('Failed to fetch master roles');
      const data = await resp.json();
      setMasterRoles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching master roles:', err);
    }
  };

  const fetchUserRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
       const tenantId = localStorage.getItem('tenant_id');
      if (!token) throw new Error('No authentication token found');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/roles/user/${userId}/tenant/${tenantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) throw new Error('Failed to fetch user roles');
      const data = await resp.json();
      
      // Map the response data with role names from master roles
      const mappedRows = Array.isArray(data) ? data.map((item, idx) => {
        const masterRole = masterRoles.find(role => role.id === item.roleId);
        return {
          id: item.id || idx + 1,
          roleId: item.roleId,
          roleName: item.roleName || item.role_name || (masterRole ? masterRole.roleName : 'Unknown Role')
        };
      }) : [];
      
      setRows(mappedRows);
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantRoles = async () => {
    setLoadingRoles(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token || !tenantId) throw new Error('Missing authentication token or tenant ID');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/roles/tenant/tenant/${tenantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) throw new Error('Failed to fetch tenant roles');
      const data = await resp.json();
      
      // Map tenant roles with role names from master roles
      const mappedRoles = Array.isArray(data) ? data.map(item => {
        const masterRole = masterRoles.find(role => role.id === item.roleId);
        return {
          roleId: item.roleId,
          roleName: item.roleName || item.role_name || (masterRole ? masterRole.roleName : 'Unknown Role')
        };
      }) : [];
      
      setAvailableRoles(mappedRoles);
    } catch (err) {
      console.error('Error fetching tenant roles:', err);
      setAvailableRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleOpenDialog = (e) => {
    if (!canAdd) return;
    if (e) e.stopPropagation();
    
    // Check if user already has a role
    if (rows.length > 0) {
      showError('User already has a role assigned. Please remove the existing role before adding a new one.', 'Cannot Add Role');
      return;
    }
    
    fetchTenantRoles();
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRoleId('');
    setAvailableRoles([]);
  };

  const handleRoleChange = (e) => {
    setSelectedRoleId(e.target.value);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedRoleId) {
      showError('Please select a role', 'Validation Error');
      return;
    }
    
    setSubmitting(true);
    try {
      const tenantId = localStorage.getItem('tenant_id');
      if (!tenantId) throw new Error('Tenant ID not found in localStorage');
      
      // Get role name for logging
      const selectedRole = availableRoles.find(role => role.roleId === selectedRoleId);
      const roleName = selectedRole ? selectedRole.roleName : 'Unknown Role';
      
      const payload = {
        tenantId: tenantId,
        roleId: selectedRoleId,
        userId: userId
      };
      
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/roles/user`;
      const token = localStorage.getItem('token');
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) throw new Error('Failed to add user role');
      
      // Log successful role addition
      const usernameToLog = username || `ID: ${userId}`;
      await AuditLoggers.userRoleAdded(usernameToLog, roleName)
        .catch(err => console.error('Error logging audit trail:', err));
      
      handleCloseDialog();
      fetchUserRoles();
      showSuccess('Role added successfully', 'Success');
    } catch (err) {
      showError(err.message || 'Failed to add user role', 'Add Role Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRole = (userRoleId, roleName) => {
    if (!canDelete) return;
    setRoleToDelete({ id: userRoleId, name: roleName });
    setDeleteDialogOpen(true);
  };

  const confirmDeleteRole = async () => {
    if (!roleToDelete) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/roles/user/${roleToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to delete user role');
      
      // Log successful role removal
      await AuditLoggers.userRoleRemoved(username || userId, roleToDelete.name)
        .catch(err => console.error('Error logging audit trail:', err));
      
      fetchUserRoles(); // Refresh the user roles list
      handleCloseDeleteDialog();
      showSuccess(`Role "${roleToDelete.name}" removed successfully`, 'Success');
    } catch (err) {
      showError(err.message || 'Failed to delete user role', 'Delete Role Failed');
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const fetchActiveCashReconProperties = async (tenantId, token) => {
    const resp = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/properties/by-tenant-and-status?tenant_id=${encodeURIComponent(tenantId)}&status=ACTIVE`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    if (!resp.ok) throw new Error('Failed to fetch active properties');
    const data = await resp.json();
    return Array.isArray(data) ? data : [];
  };

  const fetchUserTenantPropertiesList = async () => {
    setLoadingProperties(true);
    setPropertyError(null);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('Tenant ID not found in localStorage');

      const [active, assignResp] = await Promise.all([
        fetchActiveCashReconProperties(tenantId, token),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/user-tenant-properties?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        ),
      ]);

      setActiveProperties(active);
      const lookup = active.reduce((acc, p) => {
        if (p.propertyId) {
          acc[p.propertyId] = {
            name: p.propertyName || '',
            code: p.propertyCode || '',
          };
        }
        return acc;
      }, {});

      if (!assignResp.ok) throw new Error('Failed to fetch user tenant properties');
      const assignData = await assignResp.json();
      const list = Array.isArray(assignData) ? assignData : [];
      setPropertyRows(
        list.map((item, idx) => ({
          id: item.id || `row-${idx}`,
          propertyId: item.propertyId,
          propertyName: lookup[item.propertyId]?.name || item.propertyId || 'Unknown property',
          propertyCode: lookup[item.propertyId]?.code || '',
          propertyDisplay:
            lookup[item.propertyId]?.code && lookup[item.propertyId]?.name
              ? `${lookup[item.propertyId].code} - ${lookup[item.propertyId].name}`
              : lookup[item.propertyId]?.name || item.propertyId || 'Unknown property',
        }))
      );
    } catch (err) {
      setPropertyError(err.message);
      setPropertyRows([]);
    } finally {
      setLoadingProperties(false);
    }
  };

  const handleOpenPropertyDialog = async (e) => {
    if (!canAdd) return;
    if (e) e.stopPropagation();
    setPropertyDialogOpen(true);
    setSelectedPropertyIds([]);
    setPropertyPickerQuery('');
    setLoadingPropertyOptions(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token || !tenantId) {
        showError('Missing authentication token or tenant ID', 'Cannot add property');
        setPropertyDialogOpen(false);
        return;
      }
      const active = await fetchActiveCashReconProperties(tenantId, token);
      setActiveProperties(active);
    } catch (err) {
      showError(err.message || 'Failed to load properties', 'Error');
      setPropertyDialogOpen(false);
    } finally {
      setLoadingPropertyOptions(false);
    }
  };

  const handleClosePropertyDialog = () => {
    setPropertyDialogOpen(false);
    setSelectedPropertyIds([]);
    setPropertyPickerQuery('');
  };

  const propertyOptionLabel = (option) => {
    const code = option.propertyCode || option.PROPERTY_CODE || '';
    const name = option.propertyName || option.PROPERTY_NAME || '';
    return code && name ? `${code} - ${name}` : name || code || option.propertyId || '';
  };

  const handleSaveProperty = async (e) => {
    e.preventDefault();
    if (selectedPropertyIds.length === 0) {
      showError('Please select at least one property', 'Validation Error');
      return;
    }

    setSubmittingProperty(true);
    try {
      const tenantId = localStorage.getItem('tenant_id');
      const actorId = localStorage.getItem('user_id');
      const token = localStorage.getItem('token');
      if (!tenantId) throw new Error('Tenant ID not found in localStorage');
      if (!actorId) throw new Error('User ID not found - sign in again');
      if (!token) throw new Error('No authentication token found');

      const payload = selectedPropertyIds.map((propertyId) => ({
        userId,
        propertyId,
        tenantId,
        createdBy: actorId,
        updatedBy: actorId,
      }));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/user-tenant-properties/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || result.message || 'Failed to add property assignments');
      }

      const succeeded = Array.isArray(result.succeeded) ? result.succeeded : [];
      const failed = Array.isArray(result.failed) ? result.failed : [];
      const okCount = result.successCount ?? succeeded.length;
      const labelForId = (propertyId) => {
        const property = activeProperties.find((item) => item.propertyId === propertyId);
        return property ? propertyOptionLabel(property) : propertyId;
      };

      if (okCount > 0) {
        const labels = succeeded
          .map((item) => labelForId(item.propertyId))
          .filter(Boolean);
        const shown = labels.slice(0, 20).join(', ');
        const extra = labels.length > 20 ? ` and ${labels.length - 20} more` : '';
        await AuditLoggers.userPropertyAdded(username || userId, shown ? `${shown}${extra}` : `${okCount} properties`)
          .catch((err) => console.error('Error logging audit trail:', err));
        await fetchUserTenantPropertiesList();
      }

      if (failed.length === 0) {
        handleClosePropertyDialog();
        showSuccess(
          okCount === 1 ? 'Property added successfully' : `Added ${okCount} properties`,
          'Success'
        );
        return;
      }

      const failureText = failed
        .slice(0, 8)
        .map((item) => `${labelForId(item.propertyId)}: ${item.error || 'Failed'}`)
        .join('\n');
      const more = failed.length > 8 ? `\n and ${failed.length - 8} more` : '';
      setSelectedPropertyIds(failed.map((item) => item.propertyId).filter(Boolean));
      if (okCount > 0) {
        showError(
          `Added ${okCount}. ${failed.length} could not be added.\n${failureText}${more}`,
          'Some properties were not added'
        );
      } else {
        showError(`${failureText}${more}`, 'Add Property Failed');
      }
    } catch (err) {
      showError(err.message || 'Failed to add properties', 'Add Property Failed');
    } finally {
      setSubmittingProperty(false);
    }
  };

  const handleDeleteProperty = (assignmentId, propertyName) => {
    if (!canDelete) return;
    setPropertyToDelete({ id: assignmentId, name: propertyName });
    setDeletePropertyDialogOpen(true);
  };

  const handleCloseDeletePropertyDialog = () => {
    setDeletePropertyDialogOpen(false);
    setPropertyToDelete(null);
  };

  const confirmDeleteProperty = async () => {
    if (!propertyToDelete) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/user-tenant-properties/${propertyToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error('Failed to remove property assignment');

      await AuditLoggers.userPropertyRemoved(username || userId, propertyToDelete.name)
        .catch((err) => console.error('Error logging audit trail:', err));

      await fetchUserTenantPropertiesList();
      handleCloseDeletePropertyDialog();
      showSuccess(`Property "${propertyToDelete.name}" removed successfully`, 'Success');
    } catch (err) {
      showError(err.message || 'Failed to remove property', 'Delete Failed');
    }
  };

  // Re-fetch user roles when master roles are loaded
  useEffect(() => {
    if (masterRoles.length > 0 && userId && open) {
      fetchUserRoles();
    }
    // eslint-disable-next-line
  }, [masterRoles]);

  const columns = [
    { field: 'roleName', headerName: 'Role Name', flex: 1 },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => handleDeleteRole(params.row.id, params.row.roleName)}
          disabled={!canDelete}
          sx={{ color: ht.red }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const propertyColumns = [
    { field: 'propertyDisplay', headerName: 'Property', flex: 1 },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => handleDeleteProperty(params.row.id, params.row.propertyDisplay)}
          disabled={!canDelete}
          sx={{ color: ht.red }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const unassignedActiveProperties = activeProperties.filter(
    (p) => !propertyRows.some((r) => r.propertyId === p.propertyId)
  );
  const propertyPickerQueryNormalized = propertyPickerQuery.trim().toLowerCase();
  const propertyPickerRows = unassignedActiveProperties
    .filter((option) => option.propertyId)
    .filter((option) => {
      if (!propertyPickerQueryNormalized) return true;
      return propertyOptionLabel(option).toLowerCase().includes(propertyPickerQueryNormalized);
    })
    .map((option) => ({
      id: option.propertyId,
      propertyDisplay: propertyOptionLabel(option),
    }));

  const selectShownProperties = () => {
    const ids = new Set(selectedPropertyIds.map(String));
    propertyPickerRows.forEach((row) => ids.add(String(row.id)));
    setSelectedPropertyIds([...ids]);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 700,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ ...harmonateDrawerHeaderSx, mx: 0, mt: 0, borderRadius: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
          User Roles & Properties
        </Typography>
        <IconButton onClick={onClose} size="small" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={harmonateDrawerBodySx}>
        <Box sx={harmonateDrawerAccordionGroupSx}>
        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded === 'roles'}
          onChange={() => setExpanded(expanded === 'roles' ? false : 'roles')}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>User Roles</Typography>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDialog(e);
              }}
              size="small"
              disabled={!canAdd}
              sx={harmonateDrawerAddIconSx}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              ...harmonateDrawerAccordionDetailsSx,
              height: 300,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress sx={{ color: ht.blue }} />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 220 }}>
                <DataGridPro
                  rowHeight={35}
                  rows={rows}
                  columns={columns}
                  columnResizeMode="onEnd"
                  resizable
                  disableSelectionOnClick
                  sx={{
                    ...harmonateDataGridSx,
                    flex: 1,
                    '& .MuiDataGrid-cell': {
                      fontSize: '0.75rem',
                      color: ht.text,
                    },
                  }}
                  getRowId={(row) => row.id}
                  components={{
                    NoRowsOverlay: () => (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                        <Typography sx={{ color: ht.text, fontSize: '0.875rem' }}>
                          No roles assigned to this user
                        </Typography>
                      </Box>
                    ),
                  }}
                  pagination
                  pageSize={100}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded === 'properties'}
          onChange={() => setExpanded(expanded === 'properties' ? false : 'properties')}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>User Properties</Typography>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpenPropertyDialog(e);
              }}
              size="small"
              disabled={!canAdd}
              aria-label="add property"
              sx={harmonateDrawerAddIconSx}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              ...harmonateDrawerAccordionDetailsSx,
              height: 300,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {loadingProperties ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress size={28} sx={{ color: ht.blue }} />
              </Box>
            ) : propertyError ? (
              <Alert severity="error">{propertyError}</Alert>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 220 }}>
                <DataGridPro
                  rowHeight={35}
                  rows={propertyRows}
                  columns={propertyColumns}
                  columnResizeMode="onEnd"
                  resizable
                  disableSelectionOnClick
                  sx={{
                    ...harmonateDataGridSx,
                    flex: 1,
                    '& .MuiDataGrid-cell': {
                      fontSize: '0.75rem',
                      color: ht.text,
                    },
                  }}
                  getRowId={(row) => row.id}
                  components={{
                    NoRowsOverlay: () => (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                        <Typography sx={{ color: ht.text, fontSize: '0.875rem' }}>
                          No properties assigned to this user
                        </Typography>
                      </Box>
                    ),
                  }}
                  pagination
                  pageSize={50}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
        </Box>
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add User Role</DialogTitle>
        <form onSubmit={handleSave}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, minHeight: 120 }}>
            <Typography sx={{ fontSize: '0.875rem', color: ht.text, mb: 1 }}>
              Note: Users can only have one role at a time. Adding a new role will replace any existing role.
            </Typography>
            {loadingRoles ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                <CircularProgress size={24} sx={{ color: ht.blue }} />
              </Box>
            ) : (
              <FormControl fullWidth required size="small" sx={harmonateTextFieldSx}>
                <InputLabel {...harmonateInputLabelProps}>Select Role</InputLabel>
                <Select
                  value={selectedRoleId}
                  onChange={handleRoleChange}
                  label="Select Role"
                  MenuProps={harmonateSelectMenuProps}
                >
                  {availableRoles.map((role) => (
                    <MenuItem key={role.roleId} value={role.roleId}>
                      {role.roleName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || loadingRoles || !selectedRoleId}
              sx={harmonateProceedButtonSx}
            >
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ fontSize: '0.875rem', color: ht.text }}>
            Are you sure you want to remove the role "{roleToDelete?.name}" from this user?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDeleteDialog} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteRole}
            variant="contained"
            sx={harmonateAbortButtonSx}
          >
            Delete Role
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={propertyDialogOpen} onClose={handleClosePropertyDialog} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add user properties</DialogTitle>
        <form onSubmit={handleSaveProperty}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, minHeight: 120 }}>
            {loadingPropertyOptions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
                <CircularProgress size={24} sx={{ color: ht.blue }} />
              </Box>
            ) : unassignedActiveProperties.length === 0 ? (
              <Typography sx={{ fontSize: '0.875rem', color: ht.text }}>
                No additional active properties are available to assign.
              </Typography>
            ) : (
              <>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="Search by code or name"
                  value={propertyPickerQuery}
                  onChange={(event) => setPropertyPickerQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.preventDefault();
                  }}
                  disabled={submittingProperty}
                  InputLabelProps={harmonateInputLabelProps}
                  sx={harmonateTextFieldSx}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: ht.text }}>
                    {selectedPropertyIds.length} selected · {propertyPickerRows.length} shown
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      type="button"
                      onClick={selectShownProperties}
                      disabled={submittingProperty || propertyPickerRows.length === 0}
                      sx={harmonateCancelButtonSx}
                    >
                      Select all shown
                    </Button>
                    <Button
                      size="small"
                      type="button"
                      onClick={() => setSelectedPropertyIds([])}
                      disabled={submittingProperty || selectedPropertyIds.length === 0}
                      sx={harmonateCancelButtonSx}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ height: 360 }}>
                  <DataGridPro
                    rows={propertyPickerRows}
                    columns={[{ field: 'propertyDisplay', headerName: 'Property', flex: 1 }]}
                    getRowId={(row) => row.id}
                    checkboxSelection
                    disableRowSelectionOnClick
                    keepNonExistentRowsSelected
                    rowSelectionModel={selectedPropertyIds}
                    onRowSelectionModelChange={(model) => {
                      const ids = Array.isArray(model) ? model : [];
                      setSelectedPropertyIds(ids);
                    }}
                    rowHeight={35}
                    hideFooter
                    loading={submittingProperty}
                    sx={harmonateDataGridSx}
                  />
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClosePropertyDialog} disabled={submittingProperty} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                submittingProperty ||
                loadingPropertyOptions ||
                selectedPropertyIds.length === 0 ||
                unassignedActiveProperties.length === 0
              }
              sx={harmonateProceedButtonSx}
            >
              {submittingProperty
                ? 'Saving...'
                : selectedPropertyIds.length > 0
                  ? `Add (${selectedPropertyIds.length})`
                  : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deletePropertyDialogOpen} onClose={handleCloseDeletePropertyDialog} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Remove property</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ fontSize: '0.875rem', color: ht.text }}>
            Remove &quot;{propertyToDelete?.name}&quot; from this user for this tenant?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDeletePropertyDialog} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteProperty}
            variant="contained"
            sx={harmonateAbortButtonSx}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Toast 
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
        variant={toastState.variant}
      />
    </Drawer>
  );
};

export default UserRolesDrawer;